import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Mutation } from 'react-apollo';

import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Align,
  Button
} from '@sm/wds-react';
import { T, t, defineMessages } from '@sm/intl';

import uninstallAppMutation from './remove-app.graphql';

const COPY = defineMessages({
  REMOVE_DIALOG_HEADER: {
    id: 'AppsDirectory.RemoveApp.DialogHeader',
    defaultMessage: 'Remove App',
    description:
      '[Type: Header][Vis.: low] - The header of the dialog window displaying a confirmation message'
  },
  REMOVE_DIALOG_MESSAGE: {
    id: 'AppsDirectory.RemoveApp.DialogMessage',
    defaultMessage: 'Would you like to uninstall this app?',
    description:
      '[Type: Paragraph][Vis.: low] - A message asking the user to confirm if they really want to uninstall the selected app'
  },
  REMOVE_DIALOG_CONFIRM: {
    id: 'AppsDirectory.RemoveApp.UninstallButton',
    defaultMessage: 'Uninstall',
    description:
      '[Type: Button][Vis.: low] - A button confirming that user will uninstall a given application'
  },
  REMOVE_DIALOG_CANCEL: {
    id: 'AppsDirectory.RemoveApp.CancelButton',
    defaultMessage: 'Cancel',
    description:
      '[Type: Button][Vis.: low] - A button cancelling the request to uninstall a given application'
  }
});

const RemoveApp = ({ children, appId, onSuccess, onClose }) => {
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
  return (
    <>
      <div
        onClick={() => setConfirmDialogVisible(true)}
        role="button"
        tabIndex={0}
        onKeyUp={e => {
          if (e.key === 'Enter') {
            setConfirmDialogVisible(true);
          }
        }}
        data-testid="RemoveApp"
      >
        {children}
      </div>
      <Mutation
        mutation={uninstallAppMutation}
        variables={{ input: { appId } }}
        onCompleted={() => {
          setConfirmDialogVisible(false);
          onSuccess();
        }}
      >
        {(uninstallApp, { loading }) => {
          return (
            <Modal
              data-testid="RemoveApp__Dialog"
              show={confirmDialogVisible}
              size="md"
              onClose={() => {
                setConfirmDialogVisible(false);
                onClose();
              }}
            >
              <ModalHeader header={t(COPY.REMOVE_DIALOG_HEADER)} />
              <ModalBody>
                <T desc={COPY.REMOVE_DIALOG_MESSAGE} />
              </ModalBody>
              <ModalFooter>
                <Align placement="right">
                  <Button
                    color="warning"
                    onClick={uninstallApp}
                    disabled={loading || !appId}
                  >
                    <T desc={COPY.REMOVE_DIALOG_CONFIRM} />
                  </Button>
                  <Button
                    onClick={() => {
                      setConfirmDialogVisible(false);
                      onClose();
                    }}
                    color="alt"
                  >
                    <T desc={COPY.REMOVE_DIALOG_CANCEL} />
                  </Button>
                </Align>
              </ModalFooter>
            </Modal>
          );
        }}
      </Mutation>
    </>
  );
};

RemoveApp.propTypes = {
  children: PropTypes.node.isRequired,
  appId: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func
};

RemoveApp.defaultProps = {
  onClose: () => {},
  onSuccess: () => {}
};

export default RemoveApp;
